import { z } from "zod";
import { WORD_TYPES } from "../../domain/vocabulary/types.ts";

export const AI_CONTRACT_VERSION = 1 as const;

export const wordTypeSchema = z.enum(WORD_TYPES);

export const candidateMatchSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("saved"),
    existingMeaningId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("uncertain"),
    existingMeaningId: z.string().min(1),
    reason: z.string().min(1),
  }),
  z.object({
    kind: z.literal("new"),
  }),
]);

export const meaningCandidateSchema = z.object({
  id: z.string().min(1),
  type: wordTypeSchema,
  shortLabel: z.string().min(1).max(120),
  definition: z.string().min(1).max(400),
  example: z.string().min(1).max(280),
  match: candidateMatchSchema,
  contextMatch: z.boolean(),
});

export const savedSenseDescriptorSchema = z.object({
  meaningId: z.string().min(1),
  type: wordTypeSchema,
  shortLabel: z.string(),
  definition: z.string(),
  example: z.string(),
});

export const discoverMeaningsRequestSchema = z.object({
  contractVersion: z.literal(AI_CONTRACT_VERSION),
  requestId: z.string().min(1),
  word: z.string().min(1),
  normalizedWord: z.string().min(1),
  context: z.string().max(1000).optional(),
  definition: z.string().max(400).optional(),
  russianHint: z.string().max(200).optional(),
  typeHint: wordTypeSchema.optional(),
  existingSenses: z.array(savedSenseDescriptorSchema),
  previouslyShownCandidateIds: z.array(z.string()),
});

export const discoverMeaningsResultSchema = z.object({
  contractVersion: z.literal(AI_CONTRACT_VERSION),
  requestId: z.string().min(1),
  candidates: z.array(meaningCandidateSchema).max(8),
  corrections: z.array(z.string()),
  moreAvailable: z.boolean(),
});

export const selectedSenseSchema = z.object({
  itemId: z.string().min(1),
  candidateId: z.string().min(1),
  type: wordTypeSchema,
  shortLabel: z.string().min(1),
  definition: z.string().min(1),
  example: z.string().min(1),
  sourceContext: z.string().optional(),
  sourceDefinition: z.string().optional(),
  sourceHint: z.string().optional(),
  sourceType: wordTypeSchema.optional(),
});

export const comparisonCandidateSchema = z.object({
  meaningId: z.string().min(1),
  word: z.string().min(1),
  type: wordTypeSchema,
  shortLabel: z.string(),
  definition: z.string(),
  example: z.string(),
  contentRevision: z.number().int().nonnegative(),
});

export const generateMeaningPackagesRequestSchema = z.object({
  contractVersion: z.literal(AI_CONTRACT_VERSION),
  requestId: z.string().min(1),
  word: z.string().min(1),
  normalizedWord: z.string().min(1),
  senses: z.array(selectedSenseSchema).min(1),
  comparisonIndex: z.array(comparisonCandidateSchema),
  existingSenses: z.array(savedSenseDescriptorSchema),
});

export const relationProposalSchema = z.object({
  targetMeaningId: z.string().min(1),
  explanation: z.string().min(1).max(400),
});

export const generatedExerciseSchema = z.discriminatedUnion("kind", [
  z.object({
    id: z.string().min(1),
    kind: z.literal("context_recall"),
    question: z.string().min(1),
    acceptedAnswers: z.array(z.string().min(1)).min(1),
    correctAnswer: z.string().min(1),
    explanation: z.string().min(1),
    referencedMeaningIds: z.array(z.string()),
    relatedWordDistinctions: z.array(
      z.object({ meaningId: z.string(), explanation: z.string() }),
    ),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal("meaning_to_word"),
    question: z.string().min(1),
    acceptedAnswers: z.array(z.string().min(1)).min(1),
    correctAnswer: z.string().min(1),
    explanation: z.string().min(1),
    referencedMeaningIds: z.array(z.string()),
    relatedWordDistinctions: z.array(
      z.object({ meaningId: z.string(), explanation: z.string() }),
    ),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal("confusable_context"),
    question: z.string().min(1),
    acceptedAnswers: z.array(z.string().min(1)).min(1),
    correctAnswer: z.string().min(1),
    explanation: z.string().min(1),
    referencedMeaningIds: z.array(z.string()),
    relatedWordDistinctions: z.array(
      z.object({ meaningId: z.string(), explanation: z.string() }),
    ),
    options: z
      .array(z.object({ id: z.string().min(1), label: z.string().min(1) }))
      .min(2)
      .max(4),
    correctOptionId: z.string().min(1),
  }),
]);

export const meaningPackageSchema = z.object({
  itemId: z.string().min(1),
  type: wordTypeSchema,
  shortLabel: z.string().min(1).max(120),
  definition: z.string().min(1).max(400),
  russianHint: z.string().max(80).nullable(),
  example: z.string().min(1).max(280),
  memoryKey: z.string().max(160).nullable(),
  collocations: z.array(z.string().min(1).max(80)).max(3),
  relations: z.array(relationProposalSchema).max(6),
  exercises: z.array(generatedExerciseSchema).length(20),
  reviewNote: z.string().nullable(),
});

export const packageOutcomeSchema = z.discriminatedUnion("status", [
  z.object({
    itemId: z.string().min(1),
    status: z.literal("ready"),
    package: meaningPackageSchema,
  }),
  z.object({
    itemId: z.string().min(1),
    status: z.literal("conflict"),
    message: z.string().min(1),
  }),
  z.object({
    itemId: z.string().min(1),
    status: z.literal("failed"),
    code: z.string().min(1),
    message: z.string().min(1),
  }),
]);

export const generateMeaningPackagesResultSchema = z.object({
  contractVersion: z.literal(AI_CONTRACT_VERSION),
  requestId: z.string().min(1),
  outcomes: z.array(packageOutcomeSchema),
});

export type DiscoverMeaningsRequest = z.infer<typeof discoverMeaningsRequestSchema>;
export type DiscoverMeaningsResult = z.infer<typeof discoverMeaningsResultSchema>;
export type GenerateMeaningPackagesRequest = z.infer<typeof generateMeaningPackagesRequestSchema>;
export type GenerateMeaningPackagesResult = z.infer<typeof generateMeaningPackagesResultSchema>;
export type MeaningCandidate = z.infer<typeof meaningCandidateSchema>;
export type MeaningPackage = z.infer<typeof meaningPackageSchema>;
export type PackageOutcome = z.infer<typeof packageOutcomeSchema>;
export type SavedSenseDescriptor = z.infer<typeof savedSenseDescriptorSchema>;
export type SelectedSense = z.infer<typeof selectedSenseSchema>;
export type ComparisonCandidate = z.infer<typeof comparisonCandidateSchema>;
